import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";

const countyOptions = [
  "Region 1",
  "Region 2",
  "Region 3",
  "Region 4",
  "Region 5",
  "Region 6",
  "Albany",
  "Allegany",
  "Bronx",
  "Broome",
  "Cattaraugus",
  "Cayuga",
  "Chautauqua",
  "Chemung",
  "Chenango",
  "Clinton",
  "Columbia",
  "Cortland",
  "Delaware",
  "Dutchess",
  "Erie",
  "Essex",
  "Franklin",
  "Fulton",
  "Genesee",
  "Greene",
  "Hamilton",
  "Herkimer",
  "Jefferson",
  "Kings",
  "Lewis",
  "Livingston",
  "Madison",
  "Monroe",
  "Montgomery",
  "Nassau",
  "New York",
  "Niagara",
  "Oneida",
  "Onondaga",
  "Ontario",
  "Orange",
  "Orleans",
  "Oswego",
  "Otsego",
  "Putnam",
  "Queens",
  "Rensselaer",
  "Richmond",
  "Rockland",
  "Saratoga",
  "Schenectady",
  "Schoharie",
  "Schuyler",
  "Seneca",
  "St. Lawrence",
  "Steuben",
  "Suffolk",
  "Sullivan",
  "Tioga",
  "Tompkins",
  "Ulster",
  "Warren",
  "Washington",
  "Wayne",
  "Westchester",
  "Wyoming",
  "Yates",
  "Other",
];

export default function Respite() {
  const [workers, setWorkers] = useState([]);
  const [search, setSearch] = useState("");
  const [countyFilter, setCountyFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    fetchWorkers();
  }, []);

  async function fetchWorkers() {
    setLoading(true);
    setErrorMessage("");

    const { data, error } = await supabase
      .from("respite_workers")
      .select("*")
      .eq("approved", true)
      .order("last_name", { ascending: true });

    if (error) {
      console.error(error);
      setWorkers([]);
      setErrorMessage("There was an error loading respite staff.");
    } else {
      setWorkers(data || []);
    }

    setLoading(false);
  }

  function normalizeValue(value) {
    if (Array.isArray(value)) {
      return value.join(" ").toLowerCase().trim();
    }

    return String(value || "").toLowerCase().trim();
  }

  function displayValue(value) {
    if (Array.isArray(value)) {
      return value.join(", ");
    }

    return value || "Not provided";
  }

  function formatPhone(phone) {
    if (!phone) return "Not provided";

    const cleaned = String(phone).replace(/\D/g, "");

    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    return phone;
  }

  function previewList(value, visibleCount = 2) {
    if (!Array.isArray(value) || value.length === 0) return "Not provided";
    if (value.length <= visibleCount) return value.join(", ");
    return `${value.slice(0, visibleCount).join(", ")} +${value.length - visibleCount} more`;
  }

  function parseCommaSeparated(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  const filteredWorkers = useMemo(() => {
    const q = search.toLowerCase().trim();

    return workers.filter((w) => {
      const searchableText = [
        w.first_name,
        w.last_name,
        w.age,
        w.county_operating,
        w.transportation_available,
        w.years_of_experience,
        w.agencies_onboarded_with,
        w.certifications,
        w.specializations,
        w.schedule_preferences,
        w.age_groups_served,
        w.languages_spoken,
        w.phone_number,
        w.email,
      ]
        .map(normalizeValue)
        .join(" ");

      const matchesSearch = q === "" || searchableText.includes(q);

      const workerCounties = Array.isArray(w.county_operating)
        ? w.county_operating
        : [];

      const matchesCounty =
        countyFilter === "" ||
        workerCounties.includes(countyFilter) ||
        (countyFilter === "Other" &&
          workerCounties.some((c) => !countyOptions.includes(c)));

      return matchesSearch && matchesCounty;
    });
  }, [workers, search, countyFilter]);

  return (
    <div className="page">
      <section className="card">
        <h1>Respite Providers</h1>

        <p className="intro">
          Below is a list of approved respite staff available to support individuals
          and families in need of respite services.
        </p>

        <div className="controls">
          <input
            type="text"
            placeholder="Search name, county, language, certification, specialization..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search"
          />

          <select
            value={countyFilter}
            onChange={(e) => setCountyFilter(e.target.value)}
            className="county"
          >
            <option value="">All Counties</option>
            {countyOptions.map((county) => (
              <option key={county} value={county}>
                {county}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="status-message">Loading staff...</p>
        ) : errorMessage ? (
          <p className="error-message">{errorMessage}</p>
        ) : (
          <>
            
            {/* THIS CODE DISPLAY NUMBER OF PEOPLE IN LIST */}

            {/*<div className="results-count">
              {filteredWorkers.length} provider{filteredWorkers.length === 1 ? "" : "s"} found
            </div>*/}

            {/* Desktop Table */}
            <div className="tableWrap desktopOnly">
              <table>
                <thead>
                  <tr>
                    <th>Provider Name</th>
                    <th>County Operating</th>
                    <th>Agencies Onboarded With</th>
                    <th>Age Groups Served</th>
                    <th>Languages Spoken</th>
                    <th>Details</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredWorkers.length > 0 ? (
                    filteredWorkers.map((w) => (
                      <tr key={w.id}>
                        <td className="providerNameCell">
                          {w.first_name} {w.last_name}
                        </td>
                        <td>{previewList(w.county_operating, 3)}</td>
                        <td>{previewList(w.agencies_onboarded_with, 2)}</td>
                        <td>{previewList(parseCommaSeparated(w.age_groups_served), 2)}</td>
                        <td>{previewList(w.languages_spoken, 2)}</td>
                        <td>
                          <button
                            type="button"
                            className="contact-btn"
                            onClick={() => setSelectedProvider(w)}
                          >
                            Details/Contact
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-results">
                        No approved respite workers found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="mobileOnly">
              {filteredWorkers.length > 0 ? (
                <div className="mobileCards">
                  {filteredWorkers.map((w) => (
                    <div className="provider-card" key={w.id}>
                      <div className="provider-card-header">
                        <h3>
                          {w.first_name} {w.last_name}
                        </h3>
                      </div>

                      <div className="provider-card-body">
                        <p>
                          <strong>County:</strong> {previewList(w.county_operating, 3)}
                        </p>
                        <p>
                          <strong>Agencies:</strong> {previewList(w.agencies_onboarded_with, 2)}
                        </p>
                        <p>
                          <strong>Age Groups:</strong> {previewList(parseCommaSeparated(w.age_groups_served), 2)}
                        </p>
                        <p>
                          <strong>Languages:</strong> {previewList(w.languages_spoken, 2)}
                        </p>
                        <p>
                          <strong>Transportation:</strong>{" "}
                          {w.transportation_available || "Not provided"}
                        </p>
                      </div>

                      <div className="provider-card-actions">
                        <button
                          type="button"
                          className="contact-btn"
                          onClick={() => setSelectedProvider(w)}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-results">No approved respite workers found.</p>
              )}
            </div>
          </>
        )}

        {selectedProvider && (
          <div
            className="contact-modal-overlay"
            onClick={() => setSelectedProvider(null)}
          >
            <div
              className="contact-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h2>
                {selectedProvider.first_name} {selectedProvider.last_name}
              </h2>

              <p className="contact-modal-subtitle">
                Respite Provider
              </p>

              <div className="contact-highlight">
                <div className="contact-detail">
                  <strong>Email:</strong>
                  <br />
                  {selectedProvider.email ? (
                    <a href={`mailto:${selectedProvider.email}`}>
                      {selectedProvider.email}
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </div>

                <div className="contact-detail">
                  <strong>Phone:</strong>
                  <br />
                  {formatPhone(selectedProvider.phone_number)}
                </div>
              </div>

              <div className="detail-grid">
                <div className="contact-detail">
                  <strong>Age:</strong> {selectedProvider.age || "Not provided"}
                </div>

                <div className="contact-detail">
                  <strong>County Operating:</strong>{" "}
                  {displayValue(selectedProvider.county_operating)}
                </div>

                <div className="contact-detail">
                  <strong>Transportation:</strong>{" "}
                  {selectedProvider.transportation_available || "Not provided"}
                </div>

                <div className="contact-detail">
                  <strong>Years of Experience:</strong>{" "}
                  {selectedProvider.years_of_experience ?? "Not provided"}
                </div>

                <div className="contact-detail">
                  <strong>Agencies Onboarded With:</strong>{" "}
                  {displayValue(selectedProvider.agencies_onboarded_with)}
                </div>

                <div className="contact-detail">
                  <strong>Certifications:</strong>{" "}
                  {displayValue(selectedProvider.certifications)}
                </div>

                <div className="contact-detail">
                  <strong>Specializations:</strong>{" "}
                  {displayValue(selectedProvider.specializations)}
                </div>

                <div className="contact-detail">
                  <strong>Schedule Preferences:</strong>{" "}
                  {selectedProvider.schedule_preferences || "Not provided"}
                </div>

                <div className="contact-detail">
                  <strong>Age Groups Served:</strong>{" "}
                  {selectedProvider.age_groups_served || "Not provided"}
                </div>

                <div className="contact-detail">
                  <strong>Languages Spoken:</strong>{" "}
                  {displayValue(selectedProvider.languages_spoken)}
                </div>
              </div>

              <div className="modal-actions">
                {selectedProvider.email && (
                  <a
                    href={`mailto:${selectedProvider.email}`}
                    className="contact-btn email-link-btn"
                  >
                    Email Provider
                  </a>
                )}

                <button
                  type="button"
                  className="contact-btn"
                  onClick={() => setSelectedProvider(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .page {
            width: 100%;
            padding: 20px;
          }

          .card {
            background: white;
            width: 100%;
            max-width: none;
            padding: 32px;
            border-radius: 20px;
            box-shadow: 0 10px 30px rgba(0,0,0,.08);
          }

          h1 {
            text-align: center;
            margin-bottom: 10px;
            color: #111827;
          }

          .intro {
            text-align: center;
            max-width: 1000px;
            margin: 0 auto 25px auto;
            color: #4b5563;
            line-height: 1.6;
          }

          .controls {
            display: flex;
            gap: 15px;
            align-items: center;
            margin-bottom: 18px;
            flex-wrap: wrap;
          }

          .search {
            flex: 1 1 700px;
            min-width: 260px;
            padding: 14px;
            border-radius: 10px;
            border: 1px solid #ddd;
            font-size: 15px;
          }

          .county {
            flex: 0 0 220px;
            padding: 14px;
            border-radius: 10px;
            border: 1px solid #ddd;
            font-size: 15px;
          }

          .results-count {
            margin-bottom: 16px;
            color: #374151;
            font-weight: 600;
          }

          .desktopOnly {
            display: block;
          }

          .mobileOnly {
            display: none;
          }

          .tableWrap {
            width: 100%;
            overflow-x: auto;
            border: 1px solid #e5e7eb;
            border-radius: 14px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            min-width: 900px;
            background: #fff;
          }

          th {
            background: #2f6f89;
            color: white;
            padding: 14px 12px;
            text-align: left;
            font-size: 15px;
          }

          td {
            padding: 14px 12px;
            border-bottom: 1px solid #eee;
            vertical-align: top;
            color: #1f2937;
          }

          tbody tr:hover {
            background: #f7fafc;
          }

          .providerNameCell {
            font-weight: 600;
            color: #111827;
          }

          .status-message,
          .error-message,
          .no-results {
            text-align: center;
            padding: 20px;
          }

          .error-message {
            color: #a33a3a;
          }

          .contact-btn {
            background: #2f6f89;
            color: white;
            border: none;
            padding: 9px 14px;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            white-space: nowrap;
            text-decoration: none;
            display: inline-block;
          }

          .contact-btn:hover {
            background: #25596e;
          }

          .email-link-btn {
            margin-right: 10px;
          }

          .mobileCards {
            display: grid;
            gap: 14px;
          }

          .provider-card {
            background: #ffffff;
            border: 1px solid #e6edf2;
            border-radius: 16px;
            padding: 16px;
            box-shadow: 0 4px 14px rgba(0,0,0,.05);
          }

          .provider-card-header h3 {
            margin: 0 0 10px 0;
            color: #1f2937;
            font-size: 1.1rem;
          }

          .provider-card-body {
            display: grid;
            gap: 8px;
          }

          .provider-card-body p {
            margin: 0;
            line-height: 1.5;
            color: #374151;
          }

          .provider-card-actions {
            margin-top: 14px;
          }

          .contact-modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.35);
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 20px;
            z-index: 1000;
          }

          .contact-modal {
            background: white;
            width: 100%;
            max-width: 760px;
            max-height: 85vh;
            overflow-y: auto;
            padding: 28px;
            border-radius: 18px;
            box-shadow: 0 10px 40px rgba(0,0,0,.2);
          }

          .contact-modal h2 {
            margin-top: 0;
            margin-bottom: 8px;
            color: #1f2937;
            text-align: center;
          }

          .contact-modal-subtitle {
            margin-bottom: 22px;
            color: #6b7280;
            text-align: center;
          }

          .contact-highlight {
            background: #eef6fa;
            border: 1px solid #d6e6ef;
            border-radius: 14px;
            padding: 18px;
            margin-bottom: 20px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            font-size: 1.05rem;
          }

          .detail-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px 20px;
          }

          .contact-detail {
            line-height: 1.5;
            word-break: break-word;
            background: #f8fafc;
            border: 1px solid #e6edf2;
            border-radius: 12px;
            padding: 12px 14px;
          }

          .contact-highlight .contact-detail strong {
            color: #2f6f89;
          }

          .contact-detail a {
            color: #2f6f89;
          }

          .modal-actions {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 24px;
          }

          @media (max-width: 900px) {
            .page {
              padding: 12px;
            }

            .card {
              padding: 20px;
            }

            .controls {
              flex-direction: column;
              align-items: stretch;
            }

            .search,
            .county {
              width: 100%;
              min-width: 0;
              flex: 1 1 auto;
            }

            .desktopOnly {
              display: none;
            }

            .mobileOnly {
              display: block;
            }

            .detail-grid,
            .contact-highlight {
              grid-template-columns: 1fr;
            }

            .contact-modal {
              padding: 20px;
            }
          }
        `}</style>
      </section>
    </div>
  );
}